import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import classnames from "classnames";
import { CardsContext } from "../../CardsContext";
import { AppContext } from "../../../../AppContext";
import { Preloader } from "../../../../components/Preloader";
import { Button } from "../Button";
import { Socials } from "../Socials";
import { QuizzesList } from "../QuizzesList";
import { PlayList } from "../PlayList";
import { getRandomInt } from "../../utils/utils";
import { getSmartVideoHTML } from "./utils";
import {
  commentKarma,
  removeComment,
  replyComment,
} from "../../utils/comments";
import { getClass } from "../../api/getClass";
import { classWatched } from "../../api/classWatched";
import { classLike } from "../../api/classLike";
import { addComment } from "../../api/comments/addComment";
import { addCommentKarma } from "../../api/comments/addCommentKarma";
import { getComments } from "../../api/comments/getComments";
import { ArrowLeftSmallIcon } from "../../icons/ArrowLeftSmall";
import { usePrevious } from "../../../../hooks/usePrevious";
import { Comments } from "../Comments";
import { UpgradeBanner } from "../../../../components/UpgradeBanner";
import { getMediaUrl, sendPageViewEvent } from "../../../../utils/utils";
import pcpLogo from "../../../../images/pcp-logo.png";
import pcLogo from "../../../../images/pc-logo.png";
import "./Class.scss";
import BrowserLink from "../../../../components/BrowserLink";
import videoAnalytics from '../../../../utils/videoAnalytics';
import AppLink from "../../../../components/AppLink";

export function Class({ name, onBack, onSeeAll }) {
  const { cards, routes } = useContext(CardsContext);
  const { user, coaches } = useContext(AppContext);
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState(null);
  const [isShowVideoOverlay, setIsShowVideoOverlay] = useState(false);
  const prevState = usePrevious(state);
  const [comments, setComments] = useState(null);
  const [isCommentsDisabled, setIsCommentsDisabled] = useState(null);
  const [error, setError] = useState(null);
  const isWatchedLoading = useRef(false);
  const isLikeLoading = useRef(false);
  const isKarmaCommentLoading = useRef(false);
  const [height, setHeight] = useState("auto");
  const history = useHistory();

  const coach = useMemo(() => {
    if (!state) {
      return null;
    }

    return coaches.get(state.coaches_id[0]);
  }, [state, coaches]);

  const classes = useMemo(() => {
    if (!state) {
      return null;
    }

    return cards.filter((card) => card.coach[0] === state.coaches_id[0]);
  }, [state, cards]);

  const series = useMemo(() => {
    if (!state) {
      return null;
    }

    const classItem = cards.find((card) =>
      card.series.some((seria) => seria.id === state.id)
    );

    return classItem?.series || null;
  }, [state, cards]);

  const load = useCallback(async (name) => {
    try {
      setIsLoading(true);

      const data = await getClass(name);

      setState(data);
      setComments(data.comments);

      const parser = new DOMParser();
      const doc = parser.parseFromString(
        `${data.title}`,
        "text/html"
      );
      document.title = doc.body.innerText;

      sendPageViewEvent(data.title);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleTheme = () => {
    const isDarkTheme = document.body.classList.contains("pc-theme-dark");
    if (isDarkTheme) {
      iframeRef.current?.contentDocument?.body?.classList.add("pc-theme-dark");
    } else {
      iframeRef.current?.contentDocument?.body?.classList.remove(
        "pc-theme-dark"
      );
    }
  };

  useEffect(() => {
    const classObserver = new MutationObserver((mutations) => {
      mutations.forEach((mu) => {
        if (mu.type !== "attributes" && mu.attributeName !== "class") return;
        handleTheme();
      });
    });
    const body = document.querySelector("body");
    classObserver.observe(body, { attributes: true });
  }, []);

  const toggleWatched = useCallback(async () => {
    try {
      if (isWatchedLoading.current) {
        return;
      }

      // actid=20
      window.analytics?.track("watched:click", {
        "value": !state.watched,
        "action": "class_watch_flag",
        "title": state.title,
        "url": window.location.href
      });

      isWatchedLoading.current = true;

      setState((prevState) => ({
        ...prevState,
        watched: !prevState.watched,
      }));

      const response = await classWatched(state.id);

      setState((prevState) => ({
        ...prevState,
        watched: response.flag,
      }));

      isWatchedLoading.current = false;
    } catch (e) {
      setState((prevState) => ({
        ...prevState,
        watched: !prevState.watched,
      }));

      isWatchedLoading.current = false;
    }
  }, [state]);

  useEffect(() => {
    load(name);
  }, [load, name]);

  useEffect(() => {
    if (!state || state.slug === prevState?.slug) {
      return;
    }

    const playerInitIntervalId = setInterval(() => {
      const player = Object.values(window.videojs.players)[0];
      const video = document.querySelector("video");

      if (!player || !player?.duration()) {
        return;
      }

      clearInterval(playerInitIntervalId);
      window.videoInit();

      player.ready(() => {
        if (!state.has_access) {
          window.previewVideo(player, state.free_length, () => {
            setIsShowVideoOverlay(true);
          });
        }
      });

      player.ready(() => {
        const watchProgress = state.watch_progress;
        const duration = video?.duration || 0;

        if (watchProgress && duration) {
          video.currentTime = (duration * watchProgress) / 100;
        }
      });

      videoAnalytics({
        player: video,
        info: {
          userId: user.user_id,
          title: state.title,
          coach: state.coach.split(", "),
          isSeries: state.is_series,
          ...(state.is_series
            ? {
                sireiesTitle: state.series_title,
                sireiesId: state.series_id,
              }
            : {}),
        },
        videoSectionType: "class"
      });
    }, 200);

    return () => {
      Object.keys(window.videojs.players).forEach(
        (key) => delete window.videojs.players[key]
      );
    };
  }, [state, prevState, toggleWatched, user.user_id]);

  async function toggleLike() {
    try {
      if (isLikeLoading.current) {
        return;
      }

      // actid=21
      window.analytics?.track("like:click", {
        "value": !state.like_flag,
        "action": "class_like_flag",
        "title": state.title,
        "url": window.location.href
      });  

      isLikeLoading.current = true;

      setState((prevState) => ({
        ...prevState,
        like_flag: !prevState.like_flag,
      }));

      const response = await classLike(state.id);

      setState((prevState) => ({
        ...prevState,
        like_flag: response.flag,
        likes: response.likes,
      }));

      isLikeLoading.current = false;
    } catch (e) {
      setState((prevState) => ({
        ...prevState,
        like_flag: !prevState.like_flag,
      }));

      isLikeLoading.current = false;
    }
  }

  async function onKarmaComment(id, action) {
    try {
      if (isKarmaCommentLoading.current) {
        return;
      }

      // actid=23
      window.analytics?.track("like:click", {
        "value": action === 'like' ? true : false,
        "action": "comment_like_flag",
        "title": state.title,
        "url": window.location.href
      });  

      isKarmaCommentLoading.current = true;

      const result = await addCommentKarma(id, action);

      if (!result.data) {
        return;
      }

      setComments((prevComments) => {
        return {
          ...prevComments,
          comments_posts: commentKarma(prevComments.comments_posts, id, {
            likes: result.data.likes.count,
            dislikes: result.data.dislikes.count,
            flag: result.data.likes.flag
              ? "like"
              : result.data.dislikes.flag
              ? "dislike"
              : null,
          }),
        };
      });

      isKarmaCommentLoading.current = false;
    } catch (e) {
      isKarmaCommentLoading.current = false;
    }
  }

  async function onPostComment(parentId, comment) {
    const newCommentId = getRandomInt(1000000, 9999999);

    try {
      setIsCommentsDisabled(true);

      const newComment = {
        id: newCommentId,
        parent: parentId,
        date: format(new Date(), "MMM dd, yyyy"),
        content: comment,
        author: user.display_name,
        user_id: user.user_id,
        karma: {
          likes: 0,
          dislikes: 0,
        },
      };

      setComments((prevComments) => {
        let newComments;

        if (parentId === 0) {
          newComments = [newComment, ...prevComments.comments_posts];
        } else {
          newComments = replyComment(
            prevComments.comments_posts,
            parentId,
            newComment
          );
        }

        return {
          ...prevComments,
          comments_posts: newComments,
        };
      });

      await addComment(state.slug, parentId, comment);
    } catch (e) {
      setComments((prevComments) => {
        let newComments = removeComment(
          prevComments.comments_posts,
          newCommentId
        );

        return {
          ...prevComments,
          comments_posts: newComments,
        };
      });
    } finally {
      setIsCommentsDisabled(false);
    }
  }

  async function onLoadMoreComments() {
    try {
      const perPage = 10;
      const newComments = await getComments(
        state.slug,
        comments.per_page,
        perPage
      );

      if (!newComments.length) {
        return;
      }

      setComments((prevComments) => ({
        ...prevComments,
        per_page: Math.min(
          prevComments.per_page + perPage,
          prevComments.comments_posts.length + newComments.length
        ),
        comments_posts: prevComments.comments_posts.concat(newComments),
      }));
    } catch (e) {}
  }

  function handleSignInClick(e) {
    window.showPopupLogin();
    e.preventDefault();
  }

  function handleStartClick(e) {
    window.showPopupPlan({
      title: "Start your PokerCoaching membership today to unlock this class!",
      planSet:
        state.access === "Standard"
          ? "standard-premium"
          : state.access === "Premium"
          ? "premium"
          : "free",
      activePremium: state.access === "Standard",
      userAccess: user.type,
    });
    e.preventDefault();
  }

  function setIframeHeight() {
    if (!iframeRef?.current?.contentDocument?.body) {
      return;
    }

    setHeight(iframeRef.current.contentDocument.body.scrollHeight + "px");
  }

  useEffect(() => {
    const resizeIntervalId = setInterval(() => {
      setIframeHeight();
    }, 1000);

    return () => {
      clearInterval(resizeIntervalId);
    };
  }, [history, routes, state]);

  function onIframeLoad() {
    setIframeHeight();
    handleTheme();

    if (!iframeRef?.current?.contentDocument) {
      setIsLoading(false);
      return;
    }

    iframeRef.current.contentDocument.body.style.backgroundColor =
      "transparent";
    setIsLoading(false);
  }

  if (isLoading) {
    return <Preloader className="preloader" />;
  }

  if (error) {
    return (
      <div className="error">
        <p className="error__msg">{error}</p>
        <AppLink to={`${routes.home}`} className="error__btn">
          Classes
        </AppLink>
      </div>
    );
  }

  return (
    <article className="Class">
      {!state.has_access && (
        <UpgradeBanner
          access={state.access}
          description={`To watch this entire class, you must be a PokerCoaching ${state.access} member, but you can watch the first few minutes for free!`}
          title="Watch the First Few Minutes of This Class for Free!"
        />
      )}

      <Button className="Class__back" type="link" onClick={() => {
        // actid=19
        window.analytics?.track("button:click", {
          "button_name": "Back to Classes",
          "title": state.title,
          "url": window.location.href
        });

        onBack();
      }}>
        <span>
          <span className="anticon">
            <ArrowLeftSmallIcon />
          </span>{" "}
          Classes
        </span>
      </Button>

      <div className="Class__wrapper">
        <div className="Class__content">
          <div className="Class__video video-preview">
            <div
              dangerouslySetInnerHTML={{
                __html: getSmartVideoHTML(
                  state.video_src,
                  state.video_cover,
                  state.has_access
                ),
              }}
            />
            {isShowVideoOverlay && (
              <div>
                <img
                  src="https://jlsecrets.s3.amazonaws.com/img/16x9.gif"
                  alt=""
                  className="video-height-img"
                />
                <div className="video-free-overlay">
                  {state.access === "Premium" ? (
                    <img
                      src={getMediaUrl(pcpLogo)}
                      alt="pokercoaching premium"
                      className="video-free-overlay__logo"
                    />
                  ) : (
                    <img
                      src={getMediaUrl(pcLogo)}
                      alt="pokercoaching be the shark"
                      className="video-free-overlay__logo"
                    />
                  )}
                  <p className="video-free-overlay__text">
                    {user.type === "guest"
                      ? "Start your free membership to unlock this class!"
                      : "Start your PokerCoaching membership today to unlock this class!"}
                  </p>
                  <div>
                    {user.type === "guest" && (
                      <BrowserLink
                        href="/"
                        className="video-free-overlay__btn"
                        onClick={handleSignInClick}
                      >
                        Sign in
                      </BrowserLink>
                    )}
                    <BrowserLink
                      href="/"
                      className={classnames("video-free-overlay__btn", {
                        "video-free-overlay__btn_primary":
                          state.access === "Standard",
                        "video-free-overlay__btn_premium":
                          state.access === "Premium",
                      })}
                      onClick={handleStartClick}
                    >
                      {user.type === "guest"
                        ? "Start Your free membership"
                        : "Start Your Membership Today"}
                    </BrowserLink>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="Class__video-info">
            <div className="Class__video-info-main">
              <h2
                className="Class__video-title"
                dangerouslySetInnerHTML={{ __html: state.title }}
              />
              <div className="Class__video-meta">
                By {state.coach}{" "}
                <span>
                  {state.video_length} · {state.date}
                </span>
              </div>
            </div>

            <div className="Class__video-info-second">
              <button
                disabled={user.type === "guest"}
                className={classnames("Class__action-btn", {
                  "Class__action-btn_active": state.watched,
                })}
                onClick={user.type !== "guest" ? toggleWatched : undefined}
              >
                <i style={{position:'relative', top:'1px'}} className="fas fa-eye" />
              </button>

              <button
                disabled={user.type === "guest"}
                className={classnames("Class__action-btn", {
                  "Class__action-btn_active": state.like_flag,
                })}
                onClick={user.type !== "guest" ? toggleLike : undefined}
              >
                <i className="fas fa-thumbs-up" />
                <span>{state.likes}</span>
              </button>
            </div>
          </div>

          <div className="Class__video-content">
            <div dangerouslySetInnerHTML={{ __html: state.content }}></div>
          </div>

          {state.has_access && state["ld_quizzes"] && (
            <iframe
              ref={iframeRef}
              title={state.title}
              src={`${state["ld_quizzes"]}?_pc_webview=true`}
              className="quizzIframe"
              style={{ height }}
              onLoad={onIframeLoad}
            />
          )}

          <Comments
            disabled={isCommentsDisabled}
            comments={comments}
            onKarma={onKarmaComment}
            onPost={onPostComment}
            onLoadMore={onLoadMoreComments}
            title={state.title}
          />
        </div>

        <aside className="Class__aside">
          {coach && (
            <>
              <div className="Class__coach-avatar">
                <img alt={coach.title} src={coach.ava} />
              </div>

              <div className="Class__coach-name">{coach.title}</div>

              {!!coach.social?.length && <Socials socials={coach.social} />}
            </>
          )}

          {!!state.related_posts.quizzes.length && (
            <section>
              <header>
                <h2 className="Class__section-title">Quizzes</h2>
              </header>
              <QuizzesList list={state.related_posts.quizzes} />
            </section>
          )}

          {!!series?.length && (
            <section>
              <header>
                <h2 className="Class__section-title">Series</h2>
              </header>
              <PlayList activeId={state.id} list={series} redirectionClassTitle={state.title}/>
            </section>
          )}

          <section>
            <header>
              <h2 className="Class__section-title">Classes</h2>
            </header>
            <PlayList list={classes.slice(0, 3)} redirectionClassTitle={state.title}/>
            <div>
              <AppLink
                className="Class__see-all"
                to={`${routes.filter}?coach=${state.coaches_id[0]}`}
                onClick={() => {
                  // actid=26
                  window.analytics?.track("button:click", {
                    "button_name": "See all",
                    "parent_object_title": state.title,
                    "url": `${window.location.origin}${routes.filter}?coach=${state.coaches_id[0]}`
                  });

                  onSeeAll(state.coaches_id[0]);
                }}
              >
                See all ({classes.length})
              </AppLink>
            </div>
          </section>
        </aside>
      </div>
    </article>
  );
}
